import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Eye, Filter, RotateCcw } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SortableTableHead } from "../components/SortableTableHead";
import { TablePagination } from "../components/TablePagination";
import { inputClass, selectClass } from "../components/FormField";
import { admissionStatusOptions } from "../data";
import { useHospitalAdmissions } from "../hooks/useHospitalAdmissions";
import type { Admission } from "../types";

function parseAdmissionDate(date: string, time = "00:00"): Date {
  const [day, month, year] = date.split("/").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}

function parseInputDate(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function HospitalAdmissionsPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.admissions.hospitalList;
  const admissionLabels = t.safecheck.admissions;
  const { exportTable } = usePageActions();

  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("date", "desc");

  const [draftStatus, setDraftStatus] = useState("");
  const [draftDateFrom, setDraftDateFrom] = useState("");
  const [draftDateTo, setDraftDateTo] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");

  const apiFilters = useMemo(
    () => ({
      status: appliedStatus || undefined,
      date_start: appliedDateFrom || undefined,
      date_end: appliedDateTo || undefined,
    }),
    [appliedStatus, appliedDateFrom, appliedDateTo],
  );

  const { admissions: sourceAdmissions, loading, error, usingApi, total } =
    useHospitalAdmissions(apiFilters);

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const applyFilters = () => {
    setAppliedStatus(draftStatus);
    setAppliedDateFrom(draftDateFrom);
    setAppliedDateTo(draftDateTo);
    setPage(1);
  };

  const resetFilters = () => {
    setTopSearch("");
    setTableSearch("");
    setDraftStatus("");
    setDraftDateFrom("");
    setDraftDateTo("");
    setAppliedStatus("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setPage(1);
  };

  const filtered = useMemo(() => {
    const fromDate = parseInputDate(appliedDateFrom);
    const toDate = parseInputDate(appliedDateTo);
    if (toDate) {
      toDate.setHours(23, 59, 59, 999);
    }

    return sourceAdmissions.filter((admission) => {
      const admissionDate = parseAdmissionDate(admission.date, admission.time);
      const haystack =
        `${admission.patient} ${admission.patientSub} ${admission.company}`.toLowerCase();

      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      const byDateFrom = fromDate ? admissionDate >= fromDate : true;
      const byDateTo = toDate ? admissionDate <= toDate : true;
      const byStatus = appliedStatus ? admission.status === appliedStatus : true;

      return byTopSearch && byTableSearch && byDateFrom && byDateTo && byStatus;
    });
  }, [sourceAdmissions, topSearch, tableSearch, appliedDateFrom, appliedDateTo, appliedStatus]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (admission, key) => {
        if (key === "date") {
          return parseAdmissionDate(admission.date, admission.time).getTime();
        }
        if (key === "patient") return `${admission.patient} ${admission.patientSub}`;
        if (key === "type") return labels.patientTypes[admission.patientType];
        if (key === "company") return admission.company;
        if (key === "status") return admissionLabels.statuses[admission.status];
        return "";
      }),
    [filtered, sortKey, sortDirection, admissionLabels, labels.patientTypes],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const paginationLabels = labels.pagination;

  const columnDefs = [
    { key: "date", label: labels.columns.date, sortable: true },
    { key: "patient", label: labels.columns.patient, sortable: true },
    { key: "type", label: labels.columns.type, sortable: true },
    { key: "company", label: labels.columns.company, sortable: true },
    { key: "status", label: labels.columns.status, sortable: true },
    { key: "actions", label: labels.columns.actions, sortable: false },
  ] as const;

  const handleExportCsv = () => {
    exportTable(
      "admissions-hopital.csv",
      [
        labels.columns.date,
        labels.columns.patient,
        labels.columns.type,
        labels.columns.company,
        labels.columns.status,
      ],
      sorted.map((admission) => [
        `${admission.date} ${admission.time}`,
        `${admission.patient} ${admission.patientSub}`,
        labels.patientTypes[admission.patientType],
        admission.company,
        admissionLabels.statuses[admission.status],
      ]),
    );
  };

  const getPatientTypeLabel = (patientType: Admission["patientType"]) =>
    labels.patientTypes[patientType];

  const getPatientTypeBadge = (patientType: Admission["patientType"]) => {
    if (patientType === "employee") {
      return (
        <span className="inline-flex items-center rounded-md bg-purple-soft/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-purple opacity-90">
          {labels.patientTypes.employee}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-md bg-teal-soft/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal opacity-90">
        {labels.patientTypes.beneficiary}
      </span>
    );
  };

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/hospital"
        items={[
          { label: t.safecheck.nav.management },
          { label: labels.pageTitle },
        ]}
      />

      <div className="theme-card">
        <div className="border-b border-border px-5 py-4">
          <h1 className="text-base font-semibold text-foreground">{labels.pageTitle}</h1>
          {usingApi && !loading && (
            <p className="mt-1 text-xs text-muted">{total} admission{total > 1 ? "s" : ""}</p>
          )}
          {loading && <p className="mt-1 text-xs text-muted">Chargement…</p>}
          {error && <p className="mt-1 text-xs text-danger">{error}</p>}
        </div>

        <div className="flex flex-wrap items-end gap-3 border-b border-border px-5 py-4">
          <input
            type="date"
            value={draftDateFrom}
            onChange={(event) => setDraftDateFrom(event.target.value)}
            className={`${inputClass} w-full min-w-[160px] max-w-[180px]`}
          />
          <input
            type="date"
            value={draftDateTo}
            onChange={(event) => setDraftDateTo(event.target.value)}
            className={`${inputClass} w-full min-w-[160px] max-w-[180px]`}
          />
          <select
            value={draftStatus}
            onChange={(event) => setDraftStatus(event.target.value)}
            className={`${selectClass} min-w-[180px]`}
          >
            {admissionStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value ? option.label : admissionLabels.fields.allStatuses}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            <Filter size={15} />
            {t.safecheck.filter}
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
            aria-label={t.safecheck.reset}
          >
            <RotateCcw size={15} />
          </button>
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder={labels.searchPlaceholder}
            className={`${inputClass} ml-auto w-full min-w-[200px] max-w-sm`}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{t.safecheck.show}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>{t.safecheck.entries}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <Download size={15} />
              {labels.exportCsv}
            </button>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted">{t.safecheck.searchLabel}</span>
              <input
                value={tableSearch}
                onChange={(event) => {
                  setTableSearch(event.target.value);
                  setPage(1);
                }}
                className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((admission) => (
                <tr
                  key={admission.id}
                  className="border-b border-border last:border-0 hover:bg-card-muted/30"
                >
                  <td className="px-4 py-3 text-sm text-foreground">
                    <p className="font-semibold">{admission.date}</p>
                    {admission.time && admission.time !== "—" ? (
                      <p className="text-xs text-muted">{admission.time}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    <p className="font-semibold uppercase">{admission.patient}</p>
                    <p className="text-xs uppercase text-muted">{admission.patientSub}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {getPatientTypeBadge(admission.patientType)}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{admission.company}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-md bg-primary px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                      {admissionLabels.statuses[admission.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/dashboard/admissions/${admission.id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-card-muted"
                    >
                      <Eye size={14} />
                      {labels.actions.view}
                    </Link>
                  </td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={sorted.length}
          onPageChange={setPage}
          labels={paginationLabels}
        />
      </div>
    </>
  );
}
